import {
    cloneElement,
    createContext,
    useCallback,
    useContext,
    useMemo,
    useState,
    type ComponentType,
    type FC,
    type PropsWithChildren,
} from 'react';

type ModalContextValues = {
    isOpen: boolean;
    onClose: () => void;
    handleModal: HandleModal;
};

type BaseModalProps = { isOpen: boolean; onClose: () => void };

type HandleModal = <P>(
    component: ComponentType<P>,
    props?: Omit<P, keyof BaseModalProps> & {
        activeElement?: HTMLElement | null;
    }
) => void;

export const ModalProvider = ({ children }: PropsWithChildren) => {
    const [isOpen, showModal] = useState(false);
    const [Modal, setModal] = useState<any>(null);
    const [activeElement, setActiveElement] = useState<HTMLElement | null>(
        null
    );

    const onClose = useCallback(() => {
        showModal(false);
        setModal(null);

        activeElement?.focus();
    }, [showModal, activeElement]);

    const handleModal: HandleModal = useCallback((Component, props) => {
        const focusedElement =
            props?.activeElement || (document.activeElement as HTMLElement);
        setActiveElement(focusedElement);

        showModal(true);
        setModal(<Component {...(props as unknown as any)} />);
    }, []);

    const modalState = useMemo(
        () => ({ isOpen, onClose, handleModal }),
        [isOpen, onClose, handleModal]
    );

    return (
        <ModalContext.Provider value={modalState}>
            {Modal && cloneElement(Modal, { isOpen, onClose })}
            {children}
        </ModalContext.Provider>
    );
};

const ModalContext = createContext<ModalContextValues | null>(null);

export const useModal = () => {
    const modalContext = useContext(ModalContext);

    if (!modalContext) {
        throw new Error('useModal must be used within a ModalProvider');
    }

    return modalContext;
};
